// combatpro/morale/moraleEngine.js
//
// Combat PRO - Morale Engine (Stage 44: pure morale logic).
//
// Pure, UI-independent morale logic extracted from the morale runtime in
// battletracker.html (the read-only master spec). This module knows nothing
// about React, state, components, context, history, or the DOM. It contains only
// deterministic morale mathematics and the morale status metadata. It receives
// primitive values (and a monster array, read-only) and returns plain data; it
// owns no state and performs no side effects.
//
// Source mapping (battletracker.html):
//   - getMoraleStatus(morale, threshold)   from updateMoraleDisplay (9656-9722).
//                                           Reproduces the 4-band tier selection
//                                           (>=75 high, >=50 steady, >=threshold
//                                           shaken, else breaking) and the exact
//                                           bar/status colours, text, and icon
//                                           each band sets. The DOM writes,
//                                           lazy-init, and the addCombatLog on the
//                                           breaking band are NOT part of this pure
//                                           function (combat log is deferred); the
//                                           breaking band is signalled via broken.
//   - clampMorale(value)                    the Math.max(0, Math.min(100, x)) clamp
//                                           used by adjustMorale (9744) and
//                                           updateMoraleThreshold (9725).
//   - moraleLossFromDamage(oldHP,newHP,maxHP) from checkMoraleOnDamage (9757-9769):
//                                           percent HP lost; if > 10, the loss is
//                                           floor(percentLost / 10) * 5, else 0.
//   - moraleDeathPenalty(monsters, activeMonsterCount, deadIndex)
//                                           from checkMoraleOnDeath (9772-9810):
//                                           casualty percentage across active
//                                           monsters -> base penalty tier
//                                           (>=75 ->30, >=50 ->20, >=25 ->10,
//                                           else 5), doubled if the dead monster
//                                           was a leader. Returns the penalty
//                                           number only; applying it to the living
//                                           monsters is the caller's job.
//
// FORBIDDEN here and absent: React, components, context, DOM, getElementById,
// innerHTML, document, window, localStorage, API, fetch, websocket, history,
// rendering, combat log, saveState, side effects. No optional chaining. No em dash.

// Clamp a morale (or threshold) value into the inclusive 0..100 range. Matches
// the source Math.max(0, Math.min(100, x)).
export function clampMorale(value) {
  return Math.max(0, Math.min(100, value));
}

// Compute the morale status tier and its display metadata from the current
// morale and the monster's retreat threshold. Mirrors the band selection and the
// exact inline colours / text / icon set by updateMoraleDisplay (9682-9714).
// barClass maps to the vi-morale-bar CSS modifier (high / steady / shaken /
// breaking). broken is true only on the breaking band (where the source also
// writes the combat log, which is deferred and not done here).
export function getMoraleStatus(morale, threshold) {
  if (morale >= 75) {
    return {
      barClass: "high",
      barColor: "rgba(22,101,52,0.7)",
      barText: "High Morale",
      statusColor: "var(--gold-bright)",
      statusBg: "rgba(240,180,41,0.06)",
      statusText: "\u2713 Morale is strong",
      statusIcon: "\uD83D\uDCAA",
      broken: false
    };
  }
  if (morale >= 50) {
    return {
      barClass: "steady",
      barColor: "rgba(146,64,14,0.7)",
      barText: "Steady",
      statusColor: "var(--gold-bright)",
      statusBg: "rgba(240,180,41,0.06)",
      statusText: "\u26A0 Morale is wavering",
      statusIcon: "\uD83D\uDE10",
      broken: false
    };
  }
  if (morale >= threshold) {
    return {
      barClass: "shaken",
      barColor: "rgba(153,27,27,0.7)",
      barText: "Shaken",
      statusColor: "rgba(252,165,165,0.9)",
      statusBg: "rgba(153,27,27,0.12)",
      statusText: "\u26A0\uFE0F Morale is low - nearing retreat!",
      statusIcon: "\uD83D\uDE30",
      broken: false
    };
  }
  return {
    barClass: "breaking",
    barColor: "rgba(153,27,27,0.9)",
    barText: "BREAKING",
    statusColor: "rgba(252,165,165,0.9)",
    statusBg: "rgba(153,27,27,0.2)",
    statusText: "\uD83C\uDFC3 RETREAT! Morale broken!",
    statusIcon: "\uD83C\uDFC3",
    broken: true
  };
}

// Morale lost from a damage event. Mirrors checkMoraleOnDamage (9758-9767):
// percent of max HP lost; if greater than 10, the loss is floor(percentLost/10)
// * 5, otherwise 0 (the source only adjusts morale when percentLost > 10). maxHP
// falls back to 1 to avoid divide-by-zero, exactly as the source.
export function moraleLossFromDamage(oldHP, newHP, maxHP) {
  const max = maxHP || 1;
  const oldPercent = (oldHP / max) * 100;
  const newPercent = (newHP / max) * 100;
  const percentLost = oldPercent - newPercent;
  if (percentLost > 10) {
    return Math.floor(percentLost / 10) * 5;
  }
  return 0;
}

// Group morale penalty applied to each living monster when a monster dies.
// Mirrors checkMoraleOnDeath (9772-9810): counts active monsters (maxHP > 0) and
// living ones (currentHP > 0) within activeMonsterCount, derives the casualty
// percentage, selects the base penalty tier, and doubles it if the dead monster
// was a leader. Returns the penalty number; the caller applies it to the living
// monsters (combat log on leader death is deferred and not done here).
export function moraleDeathPenalty(monsters, activeMonsterCount, deadIndex) {
  const deadMonster = monsters[deadIndex];
  const wasLeader = deadMonster.isLeader;

  let activeCount = 0;
  let totalCount = 0;
  let limit = activeMonsterCount;
  if (limit > monsters.length) { limit = monsters.length; }
  for (let i = 0; i < limit; i++) {
    if (monsters[i].maxHP > 0) {
      totalCount++;
      if (monsters[i].currentHP > 0) {
        activeCount++;
      }
    }
  }

  const casualtyPercent = totalCount > 0 ? ((totalCount - activeCount) / totalCount) * 100 : 0;

  let baseMoralePenalty;
  if (casualtyPercent >= 75) {
    baseMoralePenalty = 30;
  } else if (casualtyPercent >= 50) {
    baseMoralePenalty = 20;
  } else if (casualtyPercent >= 25) {
    baseMoralePenalty = 10;
  } else {
    baseMoralePenalty = 5;
  }

  if (wasLeader) {
    baseMoralePenalty *= 2;
  }

  return baseMoralePenalty;
}
